import React from 'react'
import { Row, Col, ControlLabel, Alert } from 'react-bootstrap'

import DataDropdown from 'syft-acp-core/components/EditableTable/DataDropdown'
import EditableTextbox from 'syft-acp-core/components/EditableTextbox'
import { Button } from 'syft-acp-atoms/Button'

import { endJobAssignmentReasons } from '../EndJobAssignmentModal/structure'
import { ShiftRow } from './ShiftRow'
import { isOtherReason } from './helpers'
import { BookingCompensationDecisions, CancelReason, CompensationSaveStatus, WorkerJobBooking } from './types'

const compensationGuidance = (hasReason: boolean, autoSelectedCHCount: number) => {
  if (!hasReason) {
    return (
      <>
        Choose a cancellation reason before selecting shifts or editing Compensation Hours (CH) or Flex
        Compensation (FC).
      </>
    )
  }
  if (autoSelectedCHCount === 0) {
    return (
      <>
        <strong>No Compensation Hours (CH) defaults were applied.</strong> Review each shift's CH and Flex
        Compensation (FC) before continuing.
      </>
    )
  }

  return (
    <>
      <strong>
        Compensation Hours (CH) was automatically selected for {autoSelectedCHCount}{' '}
        {autoSelectedCHCount === 1 ? 'shift' : 'shifts'}.
      </strong>{' '}
      Review each shift's CH and Flex Compensation (FC) before continuing.
    </>
  )
}

const compensationSaveNote = (hasChanges: boolean, status: CompensationSaveStatus): string => {
  if (hasChanges) return 'Unsaved CH/FC changes'
  if (status === CompensationSaveStatus.Saved) return 'Saved'

  return 'No CH/FC changes to save'
}

// Step 1's body: pick which shifts to cancel and why. Extracted purely to keep the
// container's render complexity down — all state lives in the parent.
export const ShiftSelectStep = ({
  worker,
  isBlockBooking,
  bookings,
  allSelected,
  toggleAll,
  selected,
  toggleShift,
  compensationDecisions,
  compensationHasChanges,
  compensationSaveStatus,
  compensationSaveError,
  saveCompensation,
  changeCompensationHours,
  changeFlexCompensation,
  reason,
  reasonLabel,
  setReason,
  otherReason,
  setOtherReason,
}: {
  worker: { first_name?: string; last_name?: string }
  isBlockBooking: boolean
  bookings: WorkerJobBooking[]
  allSelected: boolean
  toggleAll: () => void
  selected: number[]
  toggleShift: (id: number) => void
  compensationDecisions: BookingCompensationDecisions
  compensationHasChanges: boolean
  compensationSaveStatus: CompensationSaveStatus
  compensationSaveError: string | undefined
  saveCompensation: () => void
  changeCompensationHours: (booking: WorkerJobBooking, enabled: boolean) => void
  changeFlexCompensation: (booking: WorkerJobBooking, enabled: boolean) => void
  reason: CancelReason
  reasonLabel: string | null | undefined
  setReason: (value: string) => void
  otherReason: string | undefined
  setOtherReason: (value?: string) => void
}) => {
  const hasReason = reason !== null
  const autoSelectedCHCount = selected.filter(shiftID => compensationDecisions[shiftID]?.chDefaulted).length

  return (
    <>
      {isBlockBooking && (
        <Row>
          <Col md={12}>
            <Alert bsStyle="info">
              <strong>Block booking:</strong> only the shifts selected below will be cancelled. To remove this
              worker from every upcoming shift shown, tick <strong>Select all shifts</strong>.
            </Alert>
          </Col>
        </Row>
      )}
      <Row>
        <Col md={12}>
          <ControlLabel>
            {worker.first_name} {worker.last_name} is booked on {bookings.length} upcoming{' '}
            {bookings.length === 1 ? 'shift' : 'shifts'}. Select which to cancel:
          </ControlLabel>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ControlLabel>Cancellation reason</ControlLabel>
          <DataDropdown
            name="reasons"
            onChange={(value: string) => setReason(value)}
            value={reason}
            editable
            options={endJobAssignmentReasons}
            showDisabled
            parameters={{ ariaLabel: 'Cancellation reason' }}
          />
        </Col>
      </Row>
      {isOtherReason(reasonLabel ?? reason) && (
        <Row>
          <Col md={12}>
            <EditableTextbox
              value={otherReason}
              placeholder="Please enter reason here"
              hasControls={false}
              onSave={(value?: string) => setOtherReason(value)}
            />
          </Col>
        </Row>
      )}

      <Row className="compensation-guidance-row">
        <Col md={12}>
          <Alert bsStyle={hasReason ? 'warning' : 'info'}>
            {compensationGuidance(hasReason, autoSelectedCHCount)}
          </Alert>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <label style={{ fontWeight: 600 }}>
            <input type="checkbox" checked={allSelected} disabled={!hasReason} onChange={toggleAll} /> Select
            all shifts
          </label>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          {bookings.map(b => (
            <ShiftRow
              key={b.shift_id}
              booking={b}
              checked={selected.includes(b.shift_id)}
              compensation={
                compensationDecisions[b.shift_id] ?? {
                  ch: b.syft_unpaid_hours_compensation,
                  chDefaulted: false,
                  fc: b.syft_compensation,
                }
              }
              reasonSelected={hasReason}
              onToggle={toggleShift}
              onChangeCH={changeCompensationHours}
              onChangeFC={changeFlexCompensation}
            />
          ))}
          <div className="compensation-save-actions">
            <Button
              kind="regular"
              onClick={saveCompensation}
              disabled={
                !hasReason ||
                !compensationHasChanges ||
                compensationSaveStatus === CompensationSaveStatus.Saving
              }
            >
              {compensationSaveStatus === CompensationSaveStatus.Saving
                ? 'Saving CH/FC changes…'
                : 'Save CH/FC changes'}
            </Button>
            <span className="compensation-save-note">
              {compensationSaveNote(compensationHasChanges, compensationSaveStatus)}
            </span>
          </div>
          {compensationSaveError && <Alert bsStyle="danger">{compensationSaveError}</Alert>}
        </Col>
      </Row>
    </>
  )
}
